import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, shareReplay, tap } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { IApiResponse } from '../../../../shared/models/api-response';
import { objectToQueryString } from '../../../../shared/utilities/object-utility';
import { ICommittee } from '../models/committee';
import { ICommitteeSearchFilter } from '../models/committee-search-filter';
import { ICommitteeSearchResult } from '../models/committee-search-result';
import { ICommitteeSearchData } from '../../../ballots/shared/models/sr/sr-committee-search-data';
import { ICommitteeHistoryDto } from '../../../../shared/constants/committee-constants';

@Injectable({
  providedIn: 'root',
})
export class CommitteeService {
  private committeeCache = new Map<
    string,
    Observable<IApiResponse<ICommittee>>
  >();
  baseUrl: string = `${environment.servicesUrl.committee}api/committees`;

  constructor(private http: HttpClient) {}

  getCommittees(
    filter?: ICommitteeSearchFilter
  ): Observable<IApiResponse<ICommitteeSearchResult[]>> {
    let queryString = '';
    if (filter) {
      queryString = objectToQueryString(filter);
    }

    return this.http.get<IApiResponse<ICommitteeSearchResult[]>>(
      `${this.baseUrl}${queryString}`
    );
  }

  getCommittee(
    committeeCode: string,
    logViewRecord?: boolean
  ): Observable<IApiResponse<ICommittee>> {
    let queryString = '';
    if (logViewRecord) {
      queryString = objectToQueryString({ logViewRecord });
    }

    return this.http.get<IApiResponse<ICommittee>>(
      `${this.baseUrl}/${committeeCode}${queryString}`
    );
  }

  getCommitteeWithCache(
    committeeCode: string,
    logViewRecord?: boolean
  ): Observable<IApiResponse<ICommittee>> {
    const normalizedCode = committeeCode.trim().toUpperCase();

    if (this.committeeCache.has(normalizedCode)) {
      return this.committeeCache.get(normalizedCode)!;
    }

    let queryString = '';
    if (logViewRecord) {
      queryString = objectToQueryString({ logViewRecord });
    }

    const request$ = this.http
      .get<IApiResponse<ICommittee>>(
        `${this.baseUrl}/${normalizedCode}${queryString}`
      )
      .pipe(
        shareReplay(1)
      );

    this.committeeCache.set(normalizedCode, request$);

    return request$;
  }

  getSectors(): Observable<IApiResponse<string[]>> {
    return this.http.get<IApiResponse<string[]>>(`${this.baseUrl}/sectors`);
  }

  getActiveCommittee(): Observable<IApiResponse<ICommitteeSearchData[]>> {
    return this.http.get<IApiResponse<ICommitteeSearchData[]>>(
      `${this.baseUrl}/active-committees`
    );
  }

  getCommitteeChangesHistories(): Observable<
    IApiResponse<ICommitteeHistoryDto[]>
  > {
    return this.http.get<IApiResponse<ICommitteeHistoryDto[]>>(
      `${this.baseUrl}/committee-changes-histories`
    );
  }
}
